import type { MouseEvent } from 'react';
import { CloseIcon } from '../../icons/CloseIcon';
import { PriceDisplay } from '../PriceDisplay';
import { Spinner } from '../Spinner';
import {
  Content,
  MetaItem,
  MetaItemLabel,
  MetaItemRemoveButton,
  MetaList,
  PriceColumn,
  RemoveButton,
  RemoveRow,
  RemoveStatus,
  SubPriceRow,
  TextColumn,
  Thumbnail,
  ThumbnailImage,
  ThumbnailPlaceholder,
  Title,
  TopRow,
  TopRowLink,
  Wrapper,
} from './BasketCard.styles';
import type { BasketCardExtra, BasketCardExtraInput, BasketCardProps } from './BasketCard.types';

export function BasketCard({
  title,
  href,
  imageUrl,
  imageAlt,
  meta,
  extras,
  price,
  subPrice,
  subPriceSuffix,
  locale,
  noImageLabel = 'No image',
  onRemove,
  removeLabel = 'Remove',
  removing = false,
  removeDisabled = false,
  removingLabel = 'Removing…',
  onRemoveExtra,
  removeExtraLabel = 'Remove',
  removingExtraKey = null,
}: BasketCardProps) {
  const normaliseExtra = (extra: BasketCardExtraInput, index: number): BasketCardExtra =>
    typeof extra === 'string' ? { key: `extra-${index}`, label: extra } : extra;

  const detailLines = [
    ...(meta ?? []).map((label, index) => ({ key: `meta-${index}`, label, extra: undefined })),
    ...(extras ?? [])
      .map(normaliseExtra)
      .map((extra) => ({ key: extra.key, label: extra.label, extra })),
  ];

  const body = (
    <>
      <Thumbnail>
        {imageUrl ? (
          <ThumbnailImage src={imageUrl} alt={imageAlt ?? title} />
        ) : (
          <ThumbnailPlaceholder aria-hidden="true">{noImageLabel}</ThumbnailPlaceholder>
        )}
      </Thumbnail>

      <Content>
        <TextColumn>
          <Title className="basket-card-title">{title}</Title>
          {detailLines.length > 0 && (
            <MetaList>
              {detailLines.map((line) => (
                <MetaItem key={line.key}>
                  <MetaItemLabel>{line.label}</MetaItemLabel>
                  {onRemoveExtra && line.extra?.removable && (
                    <MetaItemRemoveButton
                      type="button"
                      aria-label={`${removeExtraLabel} ${line.label}`}
                      disabled={removing || removeDisabled || removingExtraKey !== null}
                      onClick={(event: MouseEvent<HTMLButtonElement>) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onRemoveExtra(line.key);
                      }}
                    >
                      <CloseIcon />
                    </MetaItemRemoveButton>
                  )}
                </MetaItem>
              ))}
            </MetaList>
          )}
        </TextColumn>

        {(price || subPrice) && (
          <PriceColumn>
            {price && (
              <PriceDisplay
                amount={price.amount}
                currencyCode={price.currencyCode}
                locale={locale}
                size="base"
                weight="medium"
              />
            )}
            {subPrice && (
              <SubPriceRow>
                <PriceDisplay
                  amount={subPrice.amount}
                  currencyCode={subPrice.currencyCode}
                  locale={locale}
                  size="small"
                  weight="normal"
                />
                {subPriceSuffix ? ` ${subPriceSuffix}` : ''}
              </SubPriceRow>
            )}
          </PriceColumn>
        )}
      </Content>
    </>
  );

  return (
    <Wrapper data-testid="basket-card" data-removing={removing || undefined}>
      <TopRow>
        {href && <TopRowLink href={href} aria-label={title} />}
        {body}
      </TopRow>

      {onRemove && (
        <RemoveRow data-remove-row="true">
          {removing ? (
            <RemoveStatus role="status">
              <Spinner tone="inherit" aria-hidden />
              {removingLabel}
            </RemoveStatus>
          ) : (
            <RemoveButton
              type="button"
              aria-label={`${removeLabel}, ${title}`}
              onClick={onRemove}
              disabled={removeDisabled}
            >
              {removeLabel}
            </RemoveButton>
          )}
        </RemoveRow>
      )}
    </Wrapper>
  );
}
